import { Check } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { MetricRow } from "@/components/marketplace";
import { formatCompact, formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { CreatorProfile } from "@/hooks/useMarketplace";
import { PLATFORM_LABELS } from "../constants";
import { displayHandle, rateLabel, ProvenanceBadge } from "../CreatorPresentation";

/**
 * A selectable creator inside the campaign wizard.
 *
 * The prototypes put a "94% de afinidad" chip and a generated sentence
 * explaining the match on every row. **Neither is here**, because there is no
 * matching engine behind them — the list is the creator search filtered by the
 * brief's own countries and niches, ranked by reach. A confidence score with
 * nothing computing it is the most persuasive number on the screen and the
 * least true, which is the exact trade the provenance badges exist to refuse.
 *
 * What the row shows instead is the evidence a brand can act on: the accounts,
 * their reach, where those figures came from, and the rate.
 */
export function CreatorMatchRow({
  creator,
  selected,
  onToggle,
}: {
  creator: CreatorProfile;
  selected: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const rate = rateLabel(creator, t);

  const accounts = [...creator.socialAccounts]
    .sort((a, b) => (b.followers ?? 0) - (a.followers ?? 0))
    .slice(0, 2);

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={cn(
        "flex w-full items-start gap-4 rounded-panel border px-3.5 py-3 text-left transition-colors",
        selected
          ? "border-steel-600 bg-steel-50"
          : "border-neutral-200 bg-white hover:border-neutral-300",
      )}
    >
      {/* 72×90 — the match thumbnail ratio. Stripes where there is no photo. */}
      <span
        className={cn(
          "h-[90px] w-18 shrink-0 overflow-hidden rounded-panel bg-cover bg-center",
          !creator.avatarUrl && "adv-placeholder-thumb",
        )}
        style={
          creator.avatarUrl ? { backgroundImage: `url(${creator.avatarUrl})` } : undefined
        }
      />

      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-card-title-sm text-neutral-900">{creator.displayName}</span>
          <ProvenanceBadge account={accounts[0]} t={t} compact />
        </span>
        <span className="mt-0.5 block text-fine text-neutral-400">
          @{displayHandle(creator)}
        </span>

        {creator.bio && (
          <span className="mt-1.5 line-clamp-2 block text-fine leading-normal text-neutral-600">
            {creator.bio}
          </span>
        )}

        {accounts.length > 0 && (
          <span className="mt-2 block">
            {accounts.map((a) => (
              <MetricRow
                key={a.id}
                onImage={false}
                platform={PLATFORM_LABELS[a.platform]}
                followers={formatCompact(a.followers) ?? "—"}
                engagement={
                  a.engagementRate !== null ? formatPercent(a.engagementRate) : undefined
                }
                verified={a.dataSource === "OAUTH_VERIFIED"}
              />
            ))}
          </span>
        )}
      </span>

      <span className="flex shrink-0 flex-col items-end gap-2">
        <span
          className={cn(
            "inline-flex size-6 items-center justify-center rounded-checkbox border transition-colors",
            selected
              ? "border-steel-600 bg-steel-600 text-white"
              : "border-neutral-200 bg-white",
          )}
        >
          {selected && <Check className="size-3.5" strokeWidth={3} />}
        </span>

        {/* `rate === 0` renders "Solo canje", never "USD Canje" — the whole
            label is built in the data layer by rateLabel(). */}
        <span
          className={cn(
            "whitespace-nowrap",
            rate.known ? "text-figure-sm text-neutral-900" : "text-fine text-neutral-400",
          )}
        >
          {rate.text}
        </span>
      </span>
    </button>
  );
}
